/**
 * Sentry for the SPA (optional, build-time env-gated via VITE_SENTRY_DSN —
 * baked in by the Dockerfile/CI build args). Without a DSN initWebSentry is a
 * complete no-op. sendDefaultPii stays false — clinical app: no user identity
 * is attached to events. Prefer an EU org DSN (`*.ingest.de.sentry.io`).
 *
 * Tracing (browserTracingIntegration) and browser profiling
 * (browserProfilingIntegration, built on the JS Self-Profiling API — the
 * backend sets Document-Policy: js-profiling on the HTML document) share the
 * sample rates below.
 */
import * as Sentry from '@sentry/react';
import { wasChunkReloadRecent } from '@/lib/lazy-retry';

export const SentryErrorBoundary = Sentry.ErrorBoundary;

/** Parses a 0–1 sample rate; missing/empty/invalid values fall back. */
export function sampleRate(raw: string | undefined, fallback: number): number {
  if (raw === undefined || raw.trim() === '') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : fallback;
}

export interface WebSampleRates {
  traces: number;
  profiles: number;
}

/**
 * Defaults mirror the backend: 1.0 in dev builds, 0.1 in production
 * (quota/noise — 10% of sessions is plenty at this traffic level). Override
 * at build time with VITE_SENTRY_TRACES_SAMPLE_RATE and
 * VITE_SENTRY_PROFILES_SAMPLE_RATE.
 */
export function resolveWebSampleRates(
  env: {
    VITE_SENTRY_TRACES_SAMPLE_RATE?: string | undefined;
    VITE_SENTRY_PROFILES_SAMPLE_RATE?: string | undefined;
  },
  isProduction: boolean,
): WebSampleRates {
  const fallback = isProduction ? 0.1 : 1.0;
  return {
    traces: sampleRate(env.VITE_SENTRY_TRACES_SAMPLE_RATE, fallback),
    profiles: sampleRate(env.VITE_SENTRY_PROFILES_SAMPLE_RATE, fallback),
  };
}

/** Strip request bodies / query / cookies from browser Sentry events. */
export function scrubWebSentryEvent<
  T extends {
    request?: {
      data?: unknown;
      query_string?: unknown;
      cookies?: unknown;
      headers?: Record<string, string>;
    };
  },
>(event: T): T {
  if (event.request) {
    delete event.request.data;
    delete event.request.query_string;
    delete event.request.cookies;
    if (event.request.headers) {
      delete event.request.headers.cookie;
      delete event.request.headers.authorization;
    }
  }
  return event;
}

export function initWebSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;
  const rates = resolveWebSampleRates(
    {
      VITE_SENTRY_TRACES_SAMPLE_RATE: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as
        | string
        | undefined,
      VITE_SENTRY_PROFILES_SAMPLE_RATE: import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE as
        | string
        | undefined,
    },
    import.meta.env.PROD,
  );
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration(), Sentry.browserProfilingIntegration()],
    tracesSampleRate: rates.traces,
    // Browser profiling rides the trace lifecycle (same v10 API as the backend).
    profileSessionSampleRate: rates.profiles,
    profileLifecycle: 'trace',
    beforeSend(event) {
      // Stale hashed chunks after deploy (Mobile Safari: SUTURIA-WEB-2/4/8).
      // Drop once recovery has been scheduled; otherwise Sentry noise masks
      // real bugs while the one-shot reload is already in flight.
      const values = event.exception?.values ?? [];
      const chunky = values.some((v) =>
        /Importing a module script failed|Failed to fetch dynamically imported module|ChunkLoadError|error loading dynamically imported module/i.test(
          v.value ?? '',
        ),
      );
      if (chunky && wasChunkReloadRecent()) return null;
      return scrubWebSentryEvent(event);
    },
  });
}
